import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../context-store";
import Layout from "../../components/layout/Layout";
import "../../assets/InnerCSS/plugins/bootstrap/css/bootstrap.min.css";
import "../../assets/InnerCSS/plugins/font-awesome/css/font-awesome.min.css";
import "../../assets/InnerCSS/plugins/icomoon/style.css";
import "../../assets/InnerCSS/plugins/uniform/css/default.css";
import "../../assets/InnerCSS/plugins/switchery/switchery.min.css";
import "../../assets/InnerCSS/plugins/nvd3/nv.d3.min.css";
import "../../assets/InnerCSS/css/styles.css";
import "../../assets/InnerCSS/css/custom.css";
import AllProfilesComponent from "../../components/company/all-profiles/AllProfilesComponent";
import masterDataService from "../../services/master-data-service";
import IdNameModel from "../../models/common/IdNameModel";
import toasterUtils from "../../utils/toaster-utils";
import modalUtils from "../../utils/modal-utils";
import { Autocomplete, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useHistory } from "react-router";
import { ScreenEnum } from "../../models/enums/StatusEnum";

const AllProfiles = () => {
    const history = useHistory();
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [primarySkillList, setPrimarySkillList] = useState<IdNameModel[]>([]);
    const [refreshPrimarySkills, setRefreshPrimarySkills] = useState<number>(2);
    const [startDateSearch, setStartDateSearch] = useState<Dayjs | null>(dayjs().subtract(15, 'day'));
    const [endDateSearch, setEndDateSearch] = useState<Dayjs | null>(dayjs());
    const [primarySKillSearch, setPrimarySkillSearch] = useState<number>(0);
    const [loadAllProfilesGrid, setLoadAllProfilesGrid] = useState<number>(2);
    const [ratedCandidatesSearch, setRatedCandidatesSearch] = useState<boolean>(false);
    const [statusList, setStatusList] = useState<IdNameModel[]>([]);
    const [refreshStatus, setRefreshStatus] = useState<number>(2);

    useEffect(() => {
        loadPrimarySkills();
        getStatusList();
        clearSearch();
    }, []);

    const clearSearch = () => {
        setStartDateSearch(dayjs().subtract(15, 'day'));
        setEndDateSearch(dayjs());
        setPrimarySkillSearch(0);
        setLoadAllProfilesGrid(2);
    };

    const loadPrimarySkills = () => {
        setRefreshPrimarySkills(2);
        modalUtils.showLoader();
        masterDataService.loadPrimarySkills().then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setPrimarySkillList(res.items);
            setRefreshPrimarySkills(refreshPrimarySkills * 5);
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const searchAllProfiles = () => {
        if (startDateSearch === null || endDateSearch === null) {
            toasterUtils.showError("Please select Start Date and End Date");
            return;
        } else {
            setLoadAllProfilesGrid(loadAllProfilesGrid * 5);
        }
    };

    const getStatusList = () => {
        setRefreshStatus(2);
        masterDataService.loadStatusByScreen(ScreenEnum.AddProfileStatus).then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setStatusList(res.items);
            setRefreshStatus(refreshStatus * 5);
        }).catch((error) => {
            setRefreshStatus(refreshStatus * 5);
            toasterUtils.showError(error);
        });
    };

    const handleRatedCandidatesSearch = (): void => {
        setRatedCandidatesSearch(!ratedCandidatesSearch);
    };

    return (
        <Layout>
            <div className="breadcrumb-text">
                <a onClick={() => history.push("/")}>Home</a> <i className="fa-solid fa-angle-right"></i> All Profiles
            </div>
            <div className="page-title">
                <h4 className="breadcrumb-header"> All Profiles </h4>
            </div>
            <div className="card card-white">
                <div className="card-head">
                    <div className="row">
                        <div className="col-md-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDateSearch}
                                    onChange={(newValue) => setStartDateSearch(newValue)}
                                    format="YYYY-MMM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            margin: "normal",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-3">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="End Date"
                                    value={endDateSearch}
                                    onChange={(newValue) => setEndDateSearch(newValue)}
                                    format="YYYY-MMM-DD"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            margin: "normal",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none">
                                <Autocomplete options={primarySkillList} value={primarySkillList.find((x) => x.id === primarySKillSearch) || null}
                                    onChange={(_, newValue) => setPrimarySkillSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Primary Skill" size="small" margin="normal" fullWidth />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <FormControlLabel
                                control={
                                    <Checkbox checked={ratedCandidatesSearch} onChange={handleRatedCandidatesSearch} color="primary" />
                                }
                                label="Rated"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12 text-end">
                            <Button variant="contained" color="success" type="submit" onClick={() => searchAllProfiles()} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                                Search
                            </Button>
                            <Button variant="contained" color="warning" type="button" onClick={() => clearSearch()} startIcon={<ClearIcon />} sx={{ ml: 2, mt: 2 }}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {loadAllProfilesGrid > 2 && loggedInUser &&
                <AllProfilesComponent
                    key={loadAllProfilesGrid}
                    primarySkillList={primarySkillList}
                    statusList={statusList}
                    profileId={[loggedInUser.profileId]}
                    userId={loggedInUser.userId}
                    startDateSearch={startDateSearch ? startDateSearch.toDate() : new Date()}
                    endDateSearch={endDateSearch ? endDateSearch.toDate() : new Date()}
                    primarySkillSearch={primarySKillSearch}
                    isRated={ratedCandidatesSearch} />
            }
        </Layout>
    );
};
export default AllProfiles;